import { useCallback, useMemo, useState, type ComponentProps } from "react";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { dayStringToLocal, isChoiceFieldType, listOf } from "@salestrak/custom-fields";
import {
  Badge,
  Box,
  Button,
  formatDay,
  DND_DRAGGABLE_PROPS,
  DND_OVERLAY_PROPS,
  sortableStyle,
  Icon,
  type IconName,
  Input,
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  usePrefersReducedMotion,
} from "@salestrak/ui";

import { getOptionMetas } from "#app/components/custom-fields/option-visuals";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import {
  SORT_TRANSITION,
  followUpDragData,
  followUpRowId,
} from "#app/components/forms/form-builder/form-builder-dnd";
import { formItemGlyph } from "#app/components/forms/form-builder/form-item-identity";
import {
  useFormPaletteEntries,
  type FormPaletteEntry,
} from "#app/components/forms/form-builder/form-item-palette";
import {
  FormOptionLabel,
  formOptionProperty,
  useFormOptionEntity,
} from "#app/components/forms/form-property-options";
import type { EntityData } from "#app/lib/entity-visuals";
import {
  comparisonArity,
  conditionNeedsValues,
  conditionOpsFor,
  conditionSources,
  isComparisonComplete,
  isComparisonOp,
  countFloor,
  followUpInsertIndex,
  isFieldItem,
  makeFormItem,
  resolveItemField,
  ruleValues,
  type FormCondition,
  type FormConditionOp,
  type FormFieldItem,
  type FormItem,
} from "#app/lib/forms";

const CONDITION_OP_LABEL: Record<FormConditionOp, string> = {
  excludes: "does not include",
  includes: "includes",
  is: "is",
  isAnswered: "has an answer",
  isBlank: "is blank",
  isChecked: "is checked",
  isNot: "is not",
  isNotChecked: "is not checked",
  greaterThan: "is more than",
  lessThan: "is less than",
  between: "is between",
  before: "is before",
  after: "is after",
  withinLastDays: "is within the last",
  atLeast: "has at least",
  fewerThan: "has fewer than",
};

const COMPARISON_PICK_LABEL: Partial<Record<FormConditionOp, string>> = {
  greaterThan: "More than",
  lessThan: "Less than",
  between: "Between",
  before: "Before",
  after: "After",
  withinLastDays: "Within last",
  atLeast: "At least",
  fewerThan: "Fewer than",
};

function comparisonAnswer(condition: FormCondition): string {
  const [first = "", second = ""] = condition.values ?? [];
  switch (condition.op) {
    case "between":
      return `${first} and ${second}`;
    case "before":
    case "after":
      return formatDay(dayStringToLocal(first), { dropYear: false });
    case "withinLastDays":
      return Number(first) === 1 ? "1 day" : `${first} days`;
    case "atLeast":
    case "fewerThan":
      return Number(first) === 1 ? "1 photo" : `${first} photos`;
    default:
      return first;
  }
}

/** `info` is the one hue both a Badge and a Box background accept. */
export const BRANCH_HUE = "info";

export type ConditionDescription = {
  lead: string;
  answer: string | null;
  unfinished: boolean;
};

/** `null` when the rule is unreadable; the filler then shows the question unconditionally. */
export function describeCondition(
  condition: FormCondition,
  items: readonly FormItem[],
  itemId: string,
  customFieldsById: ReadonlyMap<string, CustomFieldDef>,
): ConditionDescription | null {
  const controller = conditionSources(items, itemId).find(
    (row) => row.itemId === condition.whenItemId,
  );
  if (!controller) return null;
  const field = resolveItemField(controller, customFieldsById);
  if (!field || !conditionOpsFor(field).includes(condition.op)) return null;
  const name = controller.label.trim() || field.name;
  const lead = `${name} ${CONDITION_OP_LABEL[condition.op]}`;
  if (isComparisonOp(condition.op)) {
    return isComparisonComplete(condition)
      ? { answer: comparisonAnswer(condition), lead, unfinished: false }
      : { answer: null, lead, unfinished: true };
  }
  if (!conditionNeedsValues(condition.op)) {
    return { answer: null, lead, unfinished: false };
  }
  const rows = field ? getOptionMetas(field) : [];
  const labels = ruleValues(condition, field).map(
    (value) => rows.find((row) => row.value === value)?.label ?? value,
  );
  if (labels.length === 0) return { answer: null, lead, unfinished: true };
  return { answer: listOf(labels, "or"), lead, unfinished: false };
}

export function ConditionTab({ description }: { description: ConditionDescription }) {
  return (
    <Box direction="row" paddingBottom="sm">
      <Box align="center" direction="row" minWidth={0} position="relative">
        {/* The answer truncates first; the hover tooltip returns the full text. */}
        <Badge className="min-w-0 shrink" color={BRANCH_HUE} size="chip" tone="soft">
          <Icon name="git-branch" size="xs" />
          <Text size="xs" weight="normal">
            {description.lead}
          </Text>
          {description.unfinished ? (
            <Text italic size="xs" weight="normal">
              not set yet
            </Text>
          ) : description.answer ? (
            <Text overflowTooltip size="xs" truncate weight="semibold">
              {description.answer}
            </Text>
          ) : null}
        </Badge>
      </Box>
    </Box>
  );
}

export const BRANCH_INDENT = 32;
const BRANCH_STROKE = 2;
/** Centred so the rail drops from under the parent card, past its rounded corner. */
const BRANCH_RAIL_LEFT = BRANCH_INDENT / 2 - BRANCH_STROKE / 2;
const BRANCH_CHIP_HEIGHT = 24;

/** One translucent group, so the elbow and rail overlap without a darker knot. */
export function BranchLine({ rail = false, elbow = false }: { rail?: boolean; elbow?: boolean }) {
  return (
    <Box inset={0} opacity={50} pointerEvents="none" position="absolute">
      {rail ? (
        <Box
          background={BRANCH_HUE}
          insetY={0}
          left={BRANCH_RAIL_LEFT}
          position="absolute"
          width={BRANCH_STROKE}
        />
      ) : null}
      {elbow ? (
        <Box
          border={["left", "bottom"]}
          className="rounded-bl-[12px] border-l-2 border-b-2 border-info"
          height={BRANCH_CHIP_HEIGHT / 2}
          left={BRANCH_RAIL_LEFT}
          position="absolute"
          top={0}
          width={BRANCH_INDENT - BRANCH_RAIL_LEFT}
        />
      ) : null}
    </Box>
  );
}

/** Must match `conditionOpsFor`, so the pane never writes a rule the chip cannot state. */
function whenOptions(
  item: FormFieldItem,
  field: CustomFieldDef | null,
): { key: string; label: string; condition: FormCondition }[] {
  const whenItemId = item.itemId;
  if (field?.type === "checkbox") {
    return [
      { condition: { op: "isChecked", whenItemId }, key: "checked", label: "Checked" },
      { condition: { op: "isNotChecked", whenItemId }, key: "unchecked", label: "Not checked" },
    ];
  }
  const any = {
    condition: { op: "isAnswered", whenItemId } satisfies FormCondition,
    key: ANY_ANSWER,
    label: ANY_ANSWER_LABEL,
  };
  const comparisons = conditionOpsFor(field)
    .filter(isComparisonOp)
    .map((op) => ({
      condition: { op, values: [], whenItemId } satisfies FormCondition,
      key: op,
      label: COMPARISON_PICK_LABEL[op] ?? op,
    }));
  if (!field || !isChoiceFieldType(field.type)) return [any, ...comparisons];
  const op: FormConditionOp = field.type === "labels" ? "includes" : "is";
  return [
    any,
    ...getOptionMetas(field).map((row) => ({
      condition: { op, values: [row.value], whenItemId },
      key: row.value,
      label: row.label,
    })),
  ];
}
const ANY_ANSWER = "__answered__";
const ANY_ANSWER_LABEL = "Any answer";
const UNPICKED_LABEL = "No answer picked";

const RULE_VALUE: Partial<Record<FormConditionOp, (answers: string | null) => string>> = {
  excludes: (answers) => (answers ? `Without ${answers}` : UNPICKED_LABEL),
  includes: (answers) => answers ?? UNPICKED_LABEL,
  is: (answers) => answers ?? UNPICKED_LABEL,
  isAnswered: () => ANY_ANSWER_LABEL,
  isBlank: () => "No answer",
  isChecked: () => "Checked",
  isNot: (answers) => (answers ? `Not ${answers}` : UNPICKED_LABEL),
  isNotChecked: () => "Not checked",
};

export function ConditionalSettings({
  item,
  items,
  customFieldsById,
  onInsert,
  onAttach,
  onReplace,
  onRule,
  onDelete,
}: {
  item: FormFieldItem;
  items: readonly FormItem[];
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  onInsert: (index: number, item: FormItem) => void;
  onAttach: (itemId: string, index: number, showWhen: FormCondition) => void;
  /** A question made here leaves the form; one built elsewhere is released. */
  onReplace: (itemId: string, next: FormItem) => void;
  onDelete: (itemId: string) => void;
  onRule: (itemIds: readonly string[], showWhen: FormCondition) => void;
}) {
  const reducedMotion = usePrefersReducedMotion();
  const field = resolveItemField(item, customFieldsById);
  const options = useMemo(() => whenOptions(item, field), [field, item]);
  const entityOf = useFormOptionEntity(formOptionProperty(item));
  const ownIndex = items.findIndex((row) => row.itemId === item.itemId);
  if (ownIndex < 0) return null;

  // Only items below this one can read it.
  const below = items.slice(ownIndex + 1);
  const readers = below.filter(
    (candidate): candidate is Followable =>
      candidate.kind !== "section" && candidate.showWhen?.whenItemId === item.itemId,
  );
  // Under this question's whole run — its last follow-up and everything
  // under that — never between a follow-up and its own follow-ups.
  const at = followUpInsertIndex(items, item.itemId);
  // A section carries no rule; adopting an item that has one rewrites it.
  const adoptable = below.filter(
    (candidate): candidate is Followable =>
      candidate.kind !== "section" && !readers.includes(candidate),
  );
  // A new follow-up starts on the first rule the shape offers.
  const born = options[0]!.condition;
  const pickable = options.length > 1;

  return (
    <Box direction="column" gap="sm">
      {readers.length > 0 ? (
        <Box direction="column">
          <RuleListHeader lead="Question" />
          <SortableContext
            items={readers.map((reader) => followUpRowId(reader.itemId))}
            strategy={verticalListSortingStrategy}
          >
            {readers.map((reader) => (
              <FollowUpRow
                adoptable={adoptable}
                controllerId={item.itemId}
                customFieldsById={customFieldsById}
                entityOf={entityOf}
                field={field}
                key={reader.itemId}
                onDelete={onDelete}
                onReplace={onReplace}
                onRule={onRule}
                options={options}
                pickable={pickable}
                reader={reader}
                reducedMotion={reducedMotion}
              />
            ))}
          </SortableContext>
        </Box>
      ) : null}
      <Box direction="row" marginLeftNegative="sm">
        <Menu>
          <MenuTrigger
            render={
              <Button size="xs" variant="ghost">
                <Icon color="muted" name="plus" size="sm" />
                <Text size="xs" truncate variant="muted" weight="normal">
                  {item.showWhen
                    ? `Add conditional question to ${item.label.trim() || "Untitled question"}`
                    : "Add conditional question"}
                </Text>
              </Button>
            }
          />
          <MenuPopup align="start">
            <QuestionMenuItems
              adoptable={adoptable}
              customFieldsById={customFieldsById}
              labels={ADD_LABELS}
              onExisting={(candidate) => onAttach(candidate.itemId, at, born)}
              onNew={(kind) => onInsert(at, { ...makeFormItem(kind), showWhen: born })}
            />
          </MenuPopup>
        </Menu>
      </Box>
    </Box>
  );
}

type Followable = Exclude<FormItem, { kind: "section" }>;

function QuestionMenuItems({
  adoptable,
  customFieldsById,
  labels,
  onNew,
  onExisting,
}: {
  adoptable: readonly Followable[];
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  labels: { created: string; existing: string };
  onNew: (kind: FormPaletteEntry["kind"]) => void;
  onExisting: (candidate: Followable) => void;
}) {
  const { questions } = useFormPaletteEntries();
  return (
    <>
      <MenuGroup>
        <MenuGroupLabel>{labels.created}</MenuGroupLabel>
        {questions.map((entry) => (
          <MenuItem
            disabled={Boolean(entry.disabledReason)}
            disabledReason={entry.disabledReason ?? undefined}
            key={entry.kind}
            onClick={() => onNew(entry.kind)}
          >
            <Icon color={entry.iconColor ?? "muted"} name={entry.icon} size="md" />
            <Text size="sm">{entry.label}</Text>
          </MenuItem>
        ))}
      </MenuGroup>
      {adoptable.length > 0 ? (
        <>
          <MenuSeparator />
          <MenuGroup>
            <MenuGroupLabel>{labels.existing}</MenuGroupLabel>
            {adoptable.map((candidate) => {
              const glyph = formItemGlyph(candidate, customFieldsById);
              return (
                <MenuItem key={candidate.itemId} onClick={() => onExisting(candidate)}>
                  <Icon color={glyph.color} name={glyph.icon} size="md" />
                  <Text size="sm" truncate>
                    {candidate.label.trim() || "Untitled question"}
                  </Text>
                </MenuItem>
              );
            })}
          </MenuGroup>
        </>
      ) : null}
    </>
  );
}
const ADD_LABELS = { created: "New question", existing: "Questions already on the form" };
const REPLACE_LABELS = {
  created: "Swap for a new question",
  existing: "Swap for a question on the form",
};

function FollowUpRow({
  reader,
  controllerId,
  customFieldsById,
  entityOf,
  field,
  options,
  pickable,
  adoptable,
  reducedMotion,
  onReplace,
  onDelete,
  onRule,
}: {
  reader: Followable;
  controllerId: string;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  entityOf: (value: string) => EntityData | null;
  field: CustomFieldDef | null;
  options: readonly { key: string; label: string; condition: FormCondition }[];
  pickable: boolean;
  adoptable: readonly Followable[];
  reducedMotion: boolean;
  onReplace: (itemId: string, next: FormItem) => void;
  onDelete: (itemId: string) => void;
  onRule: (itemIds: readonly string[], showWhen: FormCondition) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    data: followUpDragData({ controllerId, itemId: reader.itemId }),
    id: followUpRowId(reader.itemId),
    transition: reducedMotion ? null : SORT_TRANSITION,
  });
  const nodeRef = useCallback(
    (node: unknown) => setNodeRef(node as HTMLElement | null),
    [setNodeRef],
  );
  const gripRef = useCallback(
    (node: unknown) => setActivatorNodeRef(node as HTMLElement | null),
    [setActivatorNodeRef],
  );
  const name = reader.label.trim() || "Untitled question";
  const rule = reader.showWhen!;
  const readable = field ? { ...rule, values: ruleValues(rule, field) } : rule;
  const current = options.find((option) => sameCondition(option.condition, readable));
  const answerLabel = (option: (typeof options)[number], truncate: boolean) => {
    const [value, ...more] = option.condition.values ?? [];
    return (
      <FormOptionLabel
        entity={value !== undefined && more.length === 0 ? entityOf(value) : null}
        label={option.label}
        truncate={truncate}
      />
    );
  };

  return (
    <Box
      ref={nodeRef}
      border="bottom"
      direction="column"
      group="sortable-row"
      // The row itself travels in the overlay.
      opacity={isDragging ? 30 : undefined}
      style={sortableStyle(transform, { transition, isSorting })}
    >
      <Box align="center" direction="row" gap="sm" minHeight={ROW_HEIGHT} position="relative">
        <Box
          ref={gripRef}
          {...DND_DRAGGABLE_PROPS}
          {...(attributes as object)}
          {...(listeners as object)}
          align="center"
          aria-label={`Move "${name}"`}
          cursor={isDragging ? "grabbing" : "grab"}
          hoverReveal="sortable-row"
          insetY={0}
          justify="center"
          left={-PANE_GUTTER}
          position="absolute"
          revealOnTouch
          touchAction="manipulation"
          width={PANE_GUTTER - NAME_INSET}
        >
          <Icon color="faint" name="grip-vertical" size="sm" />
        </Box>
        <Box direction="row" fill="flex" marginLeftNegative="sm" minWidth={0}>
          <Menu>
            <MenuTrigger
              render={
                <RuleQuestionButton
                  customFieldsById={customFieldsById}
                  item={reader}
                  mark="chevron-down"
                />
              }
            />
            <MenuPopup align="start">
              <QuestionMenuItems
                adoptable={adoptable}
                customFieldsById={customFieldsById}
                labels={REPLACE_LABELS}
                onExisting={(candidate) =>
                  onReplace(reader.itemId, { ...candidate, showWhen: rule })
                }
                onNew={(kind) =>
                  onReplace(reader.itemId, { ...makeFormItem(kind), showWhen: rule })
                }
              />
            </MenuPopup>
          </Menu>
        </Box>
        <Box direction="row" shrink={false} width={ANSWER_WIDTH}>
          {pickable ? (
            <Select
              onValueChange={(key: string | null) => {
                const option = options.find((candidate) => candidate.key === key);
                // Re-picking the same comparison keeps its operands.
                if (option && option.key !== current?.key) {
                onRule([reader.itemId], option.condition);
              }
              }}
              value={current?.key ?? ""}
            >
              {/* `min-w-0` drops the trigger's 144px floor so long answers truncate. */}
              <SelectTrigger
                aria-label={`Show "${name}" when`}
                chevronIcon="chevron-down"
                className="min-w-0"
                size="sm"
                variant="outline"
                width="full"
              >
                <SelectValue>
                  {(key: string) => {
                    const picked = options.find((candidate) => candidate.key === key);
                    return picked ? answerLabel(picked, true) : ruleValue(rule, field);
                  }}
                </SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                {options.map((option) => (
                  <SelectItem key={option.key} value={option.key}>
                    {answerLabel(option, false)}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          ) : (
            <Text size="sm" truncate variant="muted">
              {current?.label ?? ruleValue(rule, field)}
            </Text>
          )}
        </Box>
        <Box shrink={false} width={TRASH_WIDTH}>
          <Button
            aria-label={`Remove "${name}"`}
            onClick={() => onDelete(reader.itemId)}
            size="icon-xs"
            variant="ghost"
          >
            <Icon color="faint" name="trash" size="xs" />
          </Button>
        </Box>
      </Box>
      {isComparisonOp(rule.op) ? (
        <ComparisonValues
          key={`${rule.op}:${(rule.values ?? []).join("|")}`}
          name={name}
          onCommit={(values) => onRule([reader.itemId], { ...rule, values })}
          rule={rule}
        />
      ) : null}
    </Box>
  );
}

function ComparisonValues({
  rule,
  name,
  onCommit,
}: {
  rule: FormCondition;
  name: string;
  onCommit: (values: string[]) => void;
}) {
  const day = rule.op === "before" || rule.op === "after";
  const arity = comparisonArity(rule.op);
  const [drafts, setDrafts] = useState(() =>
    Array.from({ length: arity }, (_, index) => rule.values?.[index] ?? ""),
  );
  const commit = () => {
    const stored = rule.values ?? [];
    if (drafts.every((draft, index) => draft === (stored[index] ?? ""))) return;
    onCommit(drafts.map((draft) => draft.trim()));
  };
  const field = (index: number) => (
    <Box shrink={false} width={arity === 2 ? 72 : day ? ANSWER_WIDTH + 16 : 72}>
      <Input
        aria-label={
          arity === 2
            ? `${index === 0 ? "From" : "To"} for "${name}"`
            : `Compare "${name}" against`
        }
        min={day ? undefined : (countFloor(rule.op) ?? 0)}
        onBlur={commit}
        onChange={(event) => {
          const next = event.target.value;
          setDrafts((current) => current.map((draft, at) => (at === index ? next : draft)));
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
        size="sm"
        step={countFloor(rule.op) === null ? undefined : 1}
        type={day ? "date" : "number"}
        value={drafts[index] ?? ""}
      />
    </Box>
  );
  const unit =
    rule.op === "withinLastDays"
      ? "days"
      : rule.op === "atLeast" || rule.op === "fewerThan"
        ? "photos"
        : null;
  return (
    <Box direction="row" gap="sm" justify="end" paddingBottom="sm">
      <Box align="center" direction="row" gap="xs">
        {field(0)}
        {arity === 2 ? (
          <>
            <Text size="sm" variant="muted">
              and
            </Text>
            {field(1)}
          </>
        ) : null}
        {unit ? (
          <Text size="sm" variant="muted">
            {unit}
          </Text>
        ) : null}
      </Box>
      <Box shrink={false} width={TRASH_WIDTH} />
    </Box>
  );
}

export function FollowUpRowGhost({
  itemId,
  items,
  customFieldsById,
}: {
  itemId: string;
  items: readonly FormItem[];
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
}) {
  const reader = items.find((row) => row.itemId === itemId);
  if (!reader || reader.kind === "section" || !reader.showWhen) return null;
  const rule = reader.showWhen;
  const controller = items.find((row) => row.itemId === rule.whenItemId);
  const answer = ruleAnswer(rule, controller, customFieldsById);
  const glyph = formItemGlyph(reader, customFieldsById);
  return (
    <Box
      {...DND_OVERLAY_PROPS}
      align="center"
      background="elevated"
      border
      cursor="grabbing"
      direction="row"
      gap="sm"
      minHeight={ROW_HEIGHT}
      paddingX="sm"
      rounded="md"
      shadow="lg"
    >
      <Box align="center" direction="row" fill="flex" gap="sm" minWidth={0}>
        <Icon color={glyph.color} name={glyph.icon} size="sm" />
        <Text size="sm" truncate weight="normal">
          {reader.label.trim() || "Untitled question"}
        </Text>
      </Box>
      <Box shrink={false} width={ANSWER_WIDTH}>
        <Text size="sm" truncate variant="muted">
          {answer}
        </Text>
      </Box>
      <Box shrink={false} width={TRASH_WIDTH} />
    </Box>
  );
}

function RuleListHeader({ lead }: { lead: string }) {
  return (
    <Box align="center" border="bottom" direction="row" gap="sm" paddingBottom="xs">
      <Box fill="flex" minWidth={0}>
        <Text size="xs" variant="muted">
          {lead}
        </Text>
      </Box>
      <Box shrink={false} width={ANSWER_WIDTH}>
        <Text size="xs" variant="muted">
          Shown when
        </Text>
      </Box>
      <Box shrink={false} width={TRASH_WIDTH} />
    </Box>
  );
}

/** `revealMark` shows only while a `group="row"` host is hovered. */
function RuleQuestionButton({
  item,
  customFieldsById,
  mark,
  revealMark = false,
  ...props
}: {
  item: FormItem;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  mark: IconName;
  revealMark?: boolean;
} & Omit<ComponentProps<typeof Button>, "children">) {
  const glyph = formItemGlyph(item, customFieldsById);
  return (
    <Button fullWidth justify="start" shape="square" size="sm" variant="ghost" {...props}>
      <Icon color={glyph.color} name={glyph.icon} size="sm" />
      <Text overflowTooltip size="sm" truncate weight="normal">
        {item.label.trim() || "Untitled question"}
      </Text>
      {revealMark ? (
        <Box hoverReveal="row" revealMode="display">
          <Icon color="faint" name={mark} size="xs" />
        </Box>
      ) : (
        <Icon color="faint" name={mark} size="xs" />
      )}
    </Button>
  );
}

function ruleAnswer(
  rule: FormCondition,
  controller: FormItem | undefined,
  customFieldsById: ReadonlyMap<string, CustomFieldDef>,
): string {
  const source = controller && isFieldItem(controller) ? controller : null;
  const field = source ? resolveItemField(source, customFieldsById) : null;
  if (isComparisonOp(rule.op)) {
    const named = COMPARISON_PICK_LABEL[rule.op] ?? rule.op;
    return isComparisonComplete(rule) ? `${named} ${comparisonAnswer(rule)}` : `${named}…`;
  }
  const options = source ? whenOptions(source, field) : [];
  return (
    options.find((option) => sameCondition(option.condition, rule))?.label ??
    ruleValue(rule, field)
  );
}

const ROW_HEIGHT = 36;
const PANE_GUTTER = 24;
const NAME_INSET = 8;
const ANSWER_WIDTH = 120;
/** Header, ghost and read-only row keep the trash slot so columns align. */
const TRASH_WIDTH = 24;

function ruleValue(rule: FormCondition, field: CustomFieldDef | null): string {
  const rows = field ? getOptionMetas(field) : [];
  const labels = (field ? ruleValues(rule, field) : (rule.values ?? [])).map(
    (value) => rows.find((row) => row.value === value)?.label ?? value,
  );
  const value = RULE_VALUE[rule.op];
  return value ? value(labels.length > 0 ? listOf(labels, "or") : null) : (COMPARISON_PICK_LABEL[rule.op] ?? rule.op);
}

function sameCondition(a: FormCondition, b: FormCondition): boolean {
  if (a.whenItemId !== b.whenItemId || a.op !== b.op) return false;
  if (isComparisonOp(a.op)) return true;
  const left = a.values ?? [];
  const right = b.values ?? [];
  return left.length === right.length && left.every((value) => right.includes(value));
}

export function ConditionNote({
  item,
  items,
  customFieldsById,
  onRelease,
  onSelect,
}: {
  item: FormItem;
  items: readonly FormItem[];
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  /** Seats the question below its run so later follow-ups keep their line to the one above. */
  onRelease: (itemId: string) => void;
  onSelect: (itemId: string) => void;
}) {
  const condition = item.kind === "section" ? undefined : item.showWhen;
  if (!condition) return null;

  const controller = conditionSources(items, item.itemId).find(
    (row) => row.itemId === condition.whenItemId,
  );
  const readable =
    controller && describeCondition(condition, items, item.itemId, customFieldsById) !== null;

  return (
    <Box direction="column" gap="sm">
      {controller && readable ? (
        <Box direction="column">
          <RuleListHeader lead="Follows" />
          <Box align="center" border="bottom" direction="row" gap="sm" minHeight={ROW_HEIGHT}>
            <Box
              armOnHold={false}
              direction="row"
              fill="flex"
              group="row"
              marginLeftNegative="sm"
              minWidth={0}
            >
              <RuleQuestionButton
                customFieldsById={customFieldsById}
                item={controller}
                mark="arrow-up-right"
                onClick={() => onSelect(controller.itemId)}
                revealMark
              />
            </Box>
            <Box direction="row" shrink={false} width={ANSWER_WIDTH}>
              <Text size="sm" truncate variant="muted">
                {ruleAnswer(condition, controller, customFieldsById)}
              </Text>
            </Box>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    aria-label="Remove condition"
                    onClick={() => onRelease(item.itemId)}
                    size="icon-xs"
                    variant="ghost"
                  />
                }
              >
                <Icon color="faint" name="unlink" size="xs" />
              </TooltipTrigger>
              <TooltipPopup>Remove condition</TooltipPopup>
            </Tooltip>
          </Box>
        </Box>
      ) : (
        <>
          <Text size="sm" variant="muted">
            {!controller && items.some((row) => row.itemId === condition.whenItemId)
              ? "The question this follows is now below it. Asked of everyone."
              : "The question this followed is gone. Asked of everyone."}
          </Text>
          <Box direction="row" marginLeftNegative="sm">
            <Button onClick={() => onRelease(item.itemId)} size="xs" variant="ghost">
              <Icon color="muted" name="unlink" size="sm" />
              <Text size="xs" variant="muted" weight="normal">
                Remove condition
              </Text>
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
